/**
 * Copyright © 2026 Adnuntius AS.
 */
import {LocalStorage} from "./local-storage";
import * as _ from "lodash";

type SiteCountry = {
  country: string;
  count: number;
};

type CustomEvent = {
  id: string;
  name: string;
  type: string;
  priority: number;
};

type Team = {
  id: string;
  data: Record<string, string>;
  country: string;
};

type Merchant = {
  id: string;
  title: string;
  description: string;
  link: string;
  uiTitle?: string;
};

type ProductFeedProductCount = {
  id: number;
  title: string;
  description: string;
  link: string;
  productCount: number;
  uiTitle?: string;
};

type Facet = {
  facet: string;
  count: number;
};

type MerchantDetail = {
  description: string;
  id: number;
  link: string;
  title: string;
  uiTitle?: string;
};

type RetailProductFacets = {
  brands: Array<Facet>;
  genders: Array<Facet>;
  colours: Array<Facet>;
  merchants: MerchantDetail[];
};

type NetworkInfo = {
  axProductsCount: number;
  axProductsWithReviewCount: number;
  lineItemTemplatesCount: number;
  externalDemandSourceCount: number;
  teamGroupsCount: number;
  teamsCount: number;
  advertiserCount: number;
  creativeSetCount: number;
  idsOfFullPageLayouts: number;
  teams: Array<Team>;
  contextServiceConfigurationCount: number;
  folioCount: number;
  userCount: number;
  emailTranslationsCount: number;
  scanEnabledSites: number;
  productFeedProductCounts: Array<ProductFeedProductCount>;
  merchants: Array<Merchant>;
  siteCountries: Array<SiteCountry>;
  customEvents: Array<CustomEvent>;
  retailProductFacets: RetailProductFacets;
};

const ELECTIONS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

type SupplementaryNetworkInfo = {
  themeId: string;
  elections: Array<any>;
  electionsCachedAt?: string;
};

export function isElectionsCacheStale(cachedAt: any, now: number = Date.now()): boolean {
  if (!cachedAt) {
    return true;
  }
  const cachedAtTime = _.isNumber(cachedAt) ? cachedAt : Date.parse(cachedAt);
  if (!_.isFinite(cachedAtTime)) {
    return true;
  }
  return now - cachedAtTime >= ELECTIONS_CACHE_TTL_MS;
}

function computeRetailMerchantUiTitle(text: string): string {
  const t = text || '';
  const lower = t.toLowerCase();
  if (_.includes(lower, 'blivakker')) {
    return 'Blivakker';
  }
  if (_.includes(lower, 'cocopanda')) {
    return 'Cocopanda';
  }
  return t;
}

function productFeedCountsById(counts: Array<ProductFeedProductCount>): Record<string, ProductFeedProductCount> {
  const byId: Record<string, ProductFeedProductCount> = {};
  _.forEach(counts || [], function(f) {
    if (f && _.isFinite(f.id)) {
      byId[f.id] = f;
      byId[String(f.id)] = f;
    }
  });
  return byId;
}

export class LocalNetworkInfo {
  private static instance: LocalNetworkInfo;
  private static readonly NETWORK_INFO_KEY = 'networkInfo';
  private static readonly SUPP_NETWORK_INFO_KEY = 'networkInfoSupp';

  info: NetworkInfo;
  suppInfo: SupplementaryNetworkInfo;

  private constructor() {
  }

  public static obtain(): LocalNetworkInfo {
    if (!this.instance) {
      this.instance = new LocalNetworkInfo();
    }
    this.instance.refresh();
    return this.instance;
  }

  private save() {
    LocalStorage.obtain().setItem(LocalNetworkInfo.NETWORK_INFO_KEY, this.info);
    LocalStorage.obtain().setItem(LocalNetworkInfo.SUPP_NETWORK_INFO_KEY, this.suppInfo);
  }

  private refresh() {
    const localStorageInfo = LocalStorage.obtain().getItem(LocalNetworkInfo.NETWORK_INFO_KEY);
    this.info = _.isEmpty(localStorageInfo) ? _.isEmpty(this.info) ? {} : this.info : localStorageInfo;
    this.applyProductFeedUiTitles(this.info);
    this.enrichMerchants(this.info);
    this.enrichRetailProductFacetMerchants(this.info);
    this.reorderCustomEvents();
    const localStorageSuppInfo = LocalStorage.obtain().getItem(LocalNetworkInfo.SUPP_NETWORK_INFO_KEY);
    this.suppInfo = _.isEmpty(localStorageSuppInfo) ? _.isEmpty(this.suppInfo) ? {} : this.suppInfo : localStorageSuppInfo;
  }

  increment(key: string) {
    LocalNetworkInfo.instance.refresh();
    this.info[key] = this.info[key] || 0;
    this.info[key]++;
    LocalNetworkInfo.instance.save();
  }

  setInfo(resp: NetworkInfo) {
    LocalNetworkInfo.instance.refresh();
    this.info = resp;
    this.applyProductFeedUiTitles(this.info);
    this.enrichMerchants(this.info);
    this.enrichRetailProductFacetMerchants(this.info);
    LocalNetworkInfo.instance.save();
  }

  private applyProductFeedUiTitles(info: NetworkInfo) {
    if (!info || !info.productFeedProductCounts) {
      return;
    }
    info.productFeedProductCounts = _.map(info.productFeedProductCounts, function(feed) {
      const title = feed.title || '';
      return _.assign({}, feed, {uiTitle: computeRetailMerchantUiTitle(title)});
    });
  }

  private enrichMerchants(info: NetworkInfo) {
    if (!info || !info.merchants || !info.merchants.length) {
      return;
    }
    const byId = productFeedCountsById(info.productFeedProductCounts);
    info.merchants = _.map(info.merchants, function(m) {
      const feed = byId[m.id] || byId[Number(m.id)];
      const uiTitle = feed && feed.uiTitle
        ? feed.uiTitle
        : computeRetailMerchantUiTitle(m.title || m.link || '');
      return _.assign({}, m, {uiTitle: uiTitle});
    });
  }

  private enrichRetailProductFacetMerchants(info: NetworkInfo) {
    if (!info || !info.retailProductFacets || !info.retailProductFacets.merchants || !info.retailProductFacets.merchants.length) {
      return;
    }
    const byId = productFeedCountsById(info.productFeedProductCounts);
    info.retailProductFacets = _.assign({}, info.retailProductFacets, {
      merchants: _.map(info.retailProductFacets.merchants, function(m) {
        const feed = byId[m.id] || byId[String(m.id)];
        const uiTitle = feed && feed.uiTitle
          ? feed.uiTitle
          : computeRetailMerchantUiTitle(m.title || m.link || '');
        return _.assign({}, m, {uiTitle: uiTitle});
      })
    });
  }

  getInfo(key: string) {
    return this.info[key];
  }

  getSiteCountries(): Array<SiteCountry> {
    return _.get(this.info, 'siteCountries', []) || [];
  }

  hasProductFeedProducts(): boolean {
    return _.some(this.info.productFeedProductCounts, function(productFeedProductCount) {
      return productFeedProductCount.productCount > 0;
    });
  }

  setRetailProductFacets(facets: RetailProductFacets) {
    LocalNetworkInfo.instance.refresh();
    this.info.retailProductFacets = facets;
    this.enrichRetailProductFacetMerchants(this.info);
    LocalNetworkInfo.instance.save();
  }

  getMerchantGuide() {
    const merchants = this.getInfo("merchants");
    const merchantGuide = {};
    _.forEach(merchants, function(m) {
      merchantGuide[m.id] = m;
    });
    return merchantGuide;
  }

  getSuppInfo(key: string) {
    return this.suppInfo[key];
  }

  setSuppInfo(key: string, val: any) {
    LocalNetworkInfo.instance.refresh();
    this.suppInfo = this.suppInfo || {} as SupplementaryNetworkInfo;
    this.suppInfo[key] = val;
    if (key === 'elections') {
      this.suppInfo.electionsCachedAt = new Date().toISOString();
    }
    LocalNetworkInfo.instance.save();
  }

  shouldRefreshElectionsCache(): boolean {
    LocalNetworkInfo.instance.refresh();
    return isElectionsCacheStale(_.get(this.suppInfo, 'electionsCachedAt'));
  }

  clearSuppInfo() {
    LocalNetworkInfo.instance.refresh();
    this.suppInfo = null;
    LocalNetworkInfo.instance.save();
  }

  clearNetworkCache(): void {
    this.info = {} as NetworkInfo;
    this.suppInfo = {} as SupplementaryNetworkInfo;
    LocalStorage.obtain().remove(LocalNetworkInfo.NETWORK_INFO_KEY);
    LocalStorage.obtain().remove(LocalNetworkInfo.SUPP_NETWORK_INFO_KEY);
  }

  addCustomEvent(customEvent: CustomEvent) {
    this.info.customEvents = this.info.customEvents || [];
    this.info.customEvents = this.info.customEvents.filter((ce) => {
      return ce.id !== customEvent.id;
    });
    this.info.customEvents.push({
      id: customEvent.id,
      name: customEvent.name,
      type: customEvent.type,
      priority: customEvent.priority
    });
    this.reorderCustomEvents();
    this.save();
  }

  reorderCustomEvents(){
    this.info.customEvents = this.info.customEvents || [];
    this.info.customEvents.sort((a1, a2) => {
      const a1Priority = a1.priority || 0;
      const a2Priority = a2.priority || 0;
      if (a1Priority > a2Priority) {
        return -1;
      }
      if (a1Priority < a2Priority) {
        return 1;
      }
      return 0;
    });
  }

  getTeamCountriesSet(): Array<string> {
    // Blank country values arrive as undefined/null/'' from different teams —
    // normalise them so _.uniq collapses them to a single unspecified entry.
    return _.uniq(_.map(this.info.teams || [], function (t) {
      return t.country ? t.country : 'ZZ';
    }));
  }

  showSiteCountryTargeting(): boolean {
    const siteCountries = this.getSiteCountries();
    const totalSiteCountries = siteCountries.length;
    const notableSiteCountries = _.filter(siteCountries, function (sc) {
      return sc.country && (sc.count > 1 || totalSiteCountries > 2);
    });
    return notableSiteCountries.length > 1;
  }
}
